import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePoll } from "@/hooks/usePoll";
import { apiClient } from "@/services/api";
import { handleApiError, type ApiError } from "@/utils/errorHandler";
import type { SessionRecordingSummary } from "@/types";

const RECORDING_STATUS_POLL_INTERVAL_MS = 15_000;

const sameResolutions = (
  left: number[],
  right: number[],
): boolean =>
  left.length === right.length &&
  left.every((resolution, index) => resolution === right[index]);

/**
 * Folds a polled recordings response into the current state while preserving
 * already-resolved playback URLs: whenever the playback "shape" (status +
 * resolutions) of a recording is unchanged, the previous playbackUrl is kept
 * so background polls never churn a working URL for the same stream.
 *
 * Poll path ONLY — `refreshRecordingPlayback` must replace wholesale instead,
 * because each of its responses carries a freshly minted playback token.
 */
const mergeRecordingStatuses = (
  currentRecordings: SessionRecordingSummary[],
  nextRecordings: SessionRecordingSummary[],
): SessionRecordingSummary[] =>
  nextRecordings.map((nextRecording) => {
    const currentRecording = currentRecordings.find(
      (candidate) => candidate.id === nextRecording.id,
    );
    const playbackShapeChanged =
      !currentRecording ||
      currentRecording.playbackStatus !== nextRecording.playbackStatus ||
      !sameResolutions(
        currentRecording.availableResolutions,
        nextRecording.availableResolutions,
      );
    return playbackShapeChanged
      ? nextRecording
      : {
          ...nextRecording,
          playbackUrl: currentRecording.playbackUrl,
        };
  });

/**
 * Owns the session-recordings lifecycle: initial fetch, stale-response
 * guarding across session switches, polling while recordings are
 * pending/partial (or after an error), toast-once failure handling, and the
 * explicit playback-URL refresh used by the HLS retry path.
 */
export function useSessionRecordings(
  session: { id?: string } | undefined,
): {
  recordings: SessionRecordingSummary[];
  refreshRecordingPlayback: (recordingId: string) => Promise<string | null>;
} {
  const { t } = useTranslation(["pages"]);
  const [recordings, setRecordings] = useState<SessionRecordingSummary[]>([]);
  const [recordingsError, setRecordingsError] = useState<unknown>(null);

  // Latest session whose recordings may still land in state; a fetch that
  // was in flight across a session switch must never be applied.
  const recordingsSessionIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    recordingsSessionIdRef.current = session?.id;
  }, [session?.id]);

  // Toast only the first failure of an episode: usePoll retries every 15s,
  // and toasting every retry would spam the user during an outage. Reset on
  // success or session change so a new episode toasts once again.
  const hasToastedRecordingsFailureRef = useRef(false);
  useEffect(() => {
    hasToastedRecordingsFailureRef.current = false;
  }, [session?.id]);

  const fetchRecordings = useCallback(async () => {
    if (!session?.id) {
      setRecordings([]);
      return;
    }

    try {
      const data = await apiClient.getSessionRecordings(session.id);
      if (recordingsSessionIdRef.current !== session.id) {
        return;
      }
      const nextRecordings = data.recordings || [];
      setRecordings((currentRecordings) =>
        mergeRecordingStatuses(currentRecordings, nextRecordings),
      );
      setRecordingsError(null);
      hasToastedRecordingsFailureRef.current = false;
    } catch (error) {
      if (recordingsSessionIdRef.current !== session.id) {
        return;
      }
      if (!hasToastedRecordingsFailureRef.current) {
        hasToastedRecordingsFailureRef.current = true;
        handleApiError(
          error as ApiError,
          t("pages:sessions.analytics.recordingsLoadError"),
        );
      }
      setRecordingsError(error);
    }
  }, [session?.id, t]);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  // Keep polling while any recording is still pending/partial, or until a
  // fetch succeeds again after an error (matching the previous retry loop).
  const shouldPollRecordings =
    !!session?.id &&
    (recordingsError !== null ||
      recordings.some(
        (recording) =>
          recording.playbackStatus === "pending" ||
          recording.playbackStatus === "partial",
      ));

  usePoll(fetchRecordings, {
    intervalMs: RECORDING_STATUS_POLL_INTERVAL_MS,
    enabled: shouldPollRecordings,
  });

  const refreshRecordingPlayback = useCallback(
    async (recordingId: string): Promise<string | null> => {
      if (!session?.id) {
        return null;
      }

      const data = await apiClient.getSessionRecordings(session.id);
      const nextRecordings = data.recordings || [];
      if (recordingsSessionIdRef.current !== session.id) {
        return null;
      }
      // Replace wholesale (do NOT merge): the backend mints a fresh,
      // short-lived playback token on every request, so an explicit refresh
      // must land the new URL in state even when playbackStatus and
      // resolutions are unchanged — the HLS retry path relies on the URL
      // change to reattach the player.
      setRecordings(nextRecordings);
      const refreshedRecording =
        nextRecordings.find((recording) => recording.id === recordingId) ??
        null;
      return refreshedRecording?.playbackUrl ?? null;
    },
    [session?.id],
  );

  return { recordings, refreshRecordingPlayback };
}
